import React, { useEffect, useState } from "react";
import { Button, Grid, TextField, Typography } from "@mui/material";
import { Autorenew as RegenerateIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../components/modals/AnimatedModal";

const RegenerateLabelModal = ({ isOpen, onClose, label, onSuccess }) => {
  const [formData, setFormData] = useState({
    batchNumber: "",
    manufactureDate: dayjs(),
    expirationDate: dayjs().add(7, "days"),
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const isModalOpen = isOpen;
    if (!isModalOpen) {
      return;
    }

    const hasLabel = label !== null;
    if (hasLabel) {
      const daysDifference = dayjs(label.expirationDate).diff(
        dayjs(label.manufactureDate),
        "days"
      );

      setFormData({
        batchNumber: generateNewBatchNumber(label.batchNumber),
        manufactureDate: dayjs(),
        expirationDate: dayjs().add(daysDifference, "days"),
      });
    }
  }, [label, isOpen]);

  const generateNewBatchNumber = (oldBatchNumber) => {
    const today = dayjs().format("YYYYMMDD");
    return `LOT${today}`;
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const hasError = errors[name];
    if (hasError) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const hasNoBatchNumber = !formData.batchNumber.trim();
    if (hasNoBatchNumber) {
      newErrors.batchNumber = "Número do lote é obrigatório";
    }

    const hasNoManufactureDate = !formData.manufactureDate;
    if (hasNoManufactureDate) {
      newErrors.manufactureDate = "Data de fabricação é obrigatória";
    }

    const hasNoExpirationDate = !formData.expirationDate;
    if (hasNoExpirationDate) {
      newErrors.expirationDate = "Data de validade é obrigatória";
    }

    const hasBothDates = formData.manufactureDate && formData.expirationDate;
    if (hasBothDates) {
      const manufactureDate = dayjs(formData.manufactureDate);
      const expirationDate = dayjs(formData.expirationDate);
      const isExpirationBeforeManufacture =
        expirationDate.isBefore(manufactureDate);

      if (isExpirationBeforeManufacture) {
        newErrors.expirationDate =
          "Data de validade deve ser posterior à fabricação";
      }
    }

    setErrors(newErrors);

    const hasNoErrors = Object.keys(newErrors).length === 0;
    return hasNoErrors;
  };

  const handleSubmit = async () => {
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    try {
      setLoading(true);

      const newLabelData = {
        ...label,
        id: Math.max(1000) + Math.random() * 1000,
        batchNumber: formData.batchNumber,
        manufactureDate: dayjs(formData.manufactureDate).format("YYYY-MM-DD"),
        expirationDate: dayjs(formData.expirationDate).format("YYYY-MM-DD"),
        previousBatchId: label.id,
        createdAt: new Date().toISOString(),
      };

      onSuccess("Novo lote gerado com sucesso!", newLabelData);
      onClose();
    } catch (err) {
      console.error("Erro ao gerar novo lote:", err);
      setErrors({
        submit: "Falha ao gerar novo lote. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      batchNumber: "",
      manufactureDate: dayjs(),
      expirationDate: dayjs().add(7, "days"),
    });
    setErrors({});
    onClose();
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={handleClose}
      width="700px"
      maxWidth="95%"
      zIndex={1400}
    >
      <ModalHeader
        title="Gerar Novo Lote"
        icon={RegenerateIcon}
        onClose={handleClose}
      />
      <ModalContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Gerando novo lote para o produto:{" "}
              <strong>{label?.productName}</strong>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Lote Anterior"
              value={label?.batchNumber || ""}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Novo Número do Lote"
              required
              value={formData.batchNumber}
              onChange={(e) => handleChange("batchNumber", e.target.value)}
              variant="outlined"
              error={!!errors.batchNumber}
              helperText={errors.batchNumber}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Data de Fabricação"
              value={formData.manufactureDate}
              onChange={(date) => handleChange("manufactureDate", date)}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  variant: "outlined",
                  error: !!errors.manufactureDate,
                  helperText: errors.manufactureDate,
                  disabled: loading,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Data de Validade"
              value={formData.expirationDate}
              onChange={(date) => handleChange("expirationDate", date)}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  variant: "outlined",
                  error: !!errors.expirationDate,
                  helperText: errors.expirationDate,
                  disabled: loading,
                },
              }}
            />
          </Grid>

          {errors.submit && (
            <Grid item xs={12}>
              <p style={{ color: "red" }}>{errors.submit}</p>
            </Grid>
          )}
        </Grid>
      </ModalContent>
      <ModalFooter>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ mr: 1 }}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || !formData.batchNumber.trim()}
        >
          {loading ? "Gerando..." : "Gerar Novo Lote"}
        </Button>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default RegenerateLabelModal;
